import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tabButton", "tabPanel", "accordion", "previewModal", "previewImage", "previewVideo"]
  static values = { submissionId: Number, activeProcess: String }

  connect() {
    this.storageKey = `checklist-submission-${this.submissionIdValue}`
    const saved = this.#state()
    const initialProcess = this.activeProcessValue || saved.activeProcess || this.tabButtonTargets[0]?.dataset.process

    this.#activateTab(initialProcess)
    this.#restoreAccordions(saved.openAccordions || [])

    this.boundHandleKeydown = this.#handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  switchTab(event) {
    const process = event.currentTarget.dataset.process
    this.#activateTab(process)
    this.#saveState({ activeProcess: process })

    const url = new URL(window.location.href)
    url.searchParams.set("process", process)
    window.history.replaceState({}, "", url)
  }

  toggleAccordion(event) {
    const details = event.currentTarget
    const key = details.dataset.key
    if (!key) return

    const saved = this.#state()
    let openAccordions = saved.openAccordions || []

    if (details.open) {
      if (!openAccordions.includes(key)) openAccordions.push(key)
    } else {
      openAccordions = openAccordions.filter((item) => item !== key)
    }

    this.#saveState({ openAccordions })
  }

  openMediaPreview(event) {
    if (!this.hasPreviewModalTarget || !this.hasPreviewImageTarget || !this.hasPreviewVideoTarget) return

    const { url, type } = event.params
    if (!url) return

    this.previewImageTarget.classList.add("hidden")
    this.previewVideoTarget.classList.add("hidden")

    if (type === "video") {
      this.previewVideoTarget.src = url
      this.previewVideoTarget.classList.remove("hidden")
      this.previewVideoTarget.load()
    } else {
      this.previewImageTarget.src = url
      this.previewImageTarget.classList.remove("hidden")
    }

    this.previewModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  closeMediaPreview(event) {
    if (!this.hasPreviewModalTarget || !this.hasPreviewImageTarget || !this.hasPreviewVideoTarget) return
    if (event && event.currentTarget === this.previewModalTarget && event.target !== event.currentTarget) return

    this.previewModalTarget.classList.add("hidden")
    this.previewImageTarget.src = ""
    this.previewVideoTarget.pause()
    this.previewVideoTarget.src = ""
    this.previewVideoTarget.load()
    document.body.classList.remove("overflow-hidden")
  }

  #activateTab(process) {
    this.tabButtonTargets.forEach((button) => {
      const active = button.dataset.process === process
      button.setAttribute("aria-selected", active ? "true" : "false")
      button.classList.toggle("text-blue-700", active)
      button.classList.toggle("border-blue-600", active)
      button.classList.toggle("text-gray-500", !active)
      button.classList.toggle("border-transparent", !active)
    })

    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.process !== process)
    })
  }

  #restoreAccordions(openAccordions) {
    this.accordionTargets.forEach((details) => {
      const key = details.dataset.key
      details.open = openAccordions.includes(key)
    })

    if (openAccordions.length === 0 && this.accordionTargets[0]) {
      this.accordionTargets[0].open = true
    }
  }

  #state() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || "{}")
    } catch {
      return {}
    }
  }

  #saveState(payload) {
    const merged = { ...this.#state(), ...payload }
    localStorage.setItem(this.storageKey, JSON.stringify(merged))
  }

  #handleKeydown(event) {
    if (event.key !== "Escape" || !this.hasPreviewModalTarget || this.previewModalTarget.classList.contains("hidden")) return
    this.closeMediaPreview()
  }
}
